import { create } from 'zustand';
import axios from 'axios';

const logLevel = 0; // Hier kannst du den Log-Level setzen (0-3)


const expirationMinutes: Record<string, number> = {
    uploading: 5,
    transcribing: 10,
    generating: 3,
    processing: 30,
    waiting: 30,
};


// Interface für Sitzungsdaten
interface TranscriptData {
    Text: string;
    Model: string;
}
interface SpeakerAssignmentData {
    TherapistSpeaker: string | null;
    TherapistProbability: number | null;
    Threshold: number;
    Model: string;
    Labels: Record<string, string>;
}
interface DiarizationData {
    Status: 'queued' | 'processing' | 'completed' | 'failed';
    Text: string;
    Date: string;
    SpeakerCount: number;
    IsCurrent: boolean;
    SpeakerAssignment?: SpeakerAssignmentData;
    Versions?: Record<string, DiarizationVersionData>;
}
interface DiarizationVersionData {
    Status: 'queued' | 'processing' | 'completed' | 'failed';
    Text: string;
    Date: string;
    SpeakerCount: number;
    IsCurrent: false;
    SpeakerAssignment?: SpeakerAssignmentData;
}
interface DocumentationData {
    id: number;
    date: string;
    status?: string;
    model?: string;
    prompt?: string;
    documentation: string;
    documentation_type?: string;
    template_used?: string;
    display_label?: string | null;
}
interface DocumentationMeta {
    id: number;
    date: string;
    status?: string;
    documentation_type?: string;
    documentation_label_index?: number | null;
    template_used?: string | null;
    display_label?: string | null;
}
interface SessionListData {
    id: number;
    name: string;
    date: string;
    length: number;
    status: string;
    has_documentation: boolean;
    has_transcription: boolean;
    
    tempStatus?: string;
    status_timestamp?: string;
    last_task?: string;
    template_used?: string;
    recording_type?: string;
    diarization_status?: 'queued' | 'processing' | 'completed' | 'failed';
    audio_deleted?: boolean;
    transcript_deleted?: boolean;
    documentation_locked?: boolean;
    input_source?: string;
    entity_type?: 'session' | 'case';
    kjp_context?: 'child' | 'parents' | 'family' | null;
    child_age?: number | null;

};

interface SessionData {
    id: number;
    name: string;
    date: string;
    length: string;
    status: string;
    audio_file_name?: string;
    input_source?: string;
    entity_type?: 'session' | 'case';
    gender?: 'male' | 'female' | 'other';
    client_label?: 'client' | 'patient' | 'customer';
    location?: 'in_person' | 'online' | 'clients_home' | 'other';
    session_type?: 'intake' | 'progress_note' | 'biography' | 'summary' | 'supervision';
    participation_type?: 'individual' | 'couple' | 'group' | 'other';
    kjp_context?: 'child' | 'parents' | 'family' | null;
    child_age?: number | null;
    template_used?: string;
    recording_type?: string;
    transcript_type?: string
    audio_deleted?: boolean;
    transcript_deleted?: boolean;
    documentation_locked?: boolean;

    documentation?: DocumentationData | null;
    documentation_list?: DocumentationMeta[];

    data?: {
        Transcript?: TranscriptData;
        Diarization?: DiarizationData;
        [key: string]: any;
    };
}

const getTempStatusStore = (): Record<string, string> => {
    try {
        return JSON.parse(localStorage.getItem('tempStatusMap') || '{}');
    } catch {
        return {};
    }
};

const removeTempStatusMetadata = (id: number) => {
    const tempStatusStore = getTempStatusStore();
    delete tempStatusStore[id];
    localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusStore));
    localStorage.removeItem(`tempStatusTime_${id}`);
    localStorage.removeItem(`tempStatusExpire_${id}`);
};

const applyTempStatusResolution = (session: SessionListData, tempStatusStore: Record<string, string>): SessionListData => {
    const tempStatus = tempStatusStore[session.id];
    const backendTimestamp = session.status_timestamp ? new Date(session.status_timestamp).getTime() : 0;
    const tempTimestampKey = `tempStatusTime_${session.id}`;
    const tempTimestamp = parseInt(localStorage.getItem(tempTimestampKey) || '0');
    const expireKey = `tempStatusExpire_${session.id}`;
    const expireTimestamp = parseInt(localStorage.getItem(expireKey) || '0');
    const isExpired = Date.now() > expireTimestamp;

    let resolvedStatus = session.status;
    if (tempStatus && tempTimestamp > backendTimestamp && !isExpired) {
        resolvedStatus = tempStatus;
    } else {
        delete tempStatusStore[session.id];
        localStorage.removeItem(tempTimestampKey);
        localStorage.removeItem(expireKey);
    }

    return {
        ...session,
        tempStatus: resolvedStatus,
    };
};

const sortSessionsByDateDesc = (sessions: SessionListData[]) =>
    [...sessions].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

// Zustand-Store für Sitzungsdaten
interface SessionStore {
    sessionData: SessionData | null;
    sessions: SessionListData[]; // Liste aller Sessions
    isSessionSelected: boolean; // ✅ Neuer Zustand für Sitzungsauswahl
    selectedSessionId: number | null; // 👈 NEU
    selectedDocId: number | null;
    isAudioDialogOpen: boolean;
    setIsAudioDialogOpen: (open: boolean) => void;

    setIsSessionSelected: (selected: boolean) => void; // ✅ Funktion zum Ändern des Werts
    setSelectedSessionId: (id: number | null) => void; // 👈 NEU
    setSelectedDocId: (id: number | null) => void;
    setSessions: (sessions: SessionListData[]) => void;
    fetchSessions: () => Promise<SessionListData[] | null>;
    fetchSessionListItem: (id: number) => Promise<SessionListData | null>;
    fetchSessionDataById: (id: number, docId?: number | null) => Promise<SessionData | null>;
    updateSession: (id: number, updatedFields: Partial<SessionData>) => Promise<boolean>;
    updateSessionTempStatus: (id: number, status: string) => void;
    upsertSessionInList: (session: SessionListData) => void;
    removeSessionFromList: (id: number) => void;
    clearTempStatus: (id: number) => void;
    clearSelectedSession: () => void;
    reset: () => void;
}

// Zustand Store definieren
export const useSessionStore = create<SessionStore>((set, get) => ({
    
    
    sessionData: null,
    isSessionSelected: false, // Standardwert: keine Sitzung ausgewählt
    selectedSessionId: null,
    selectedDocId: null,
    sessions: [],
    isAudioDialogOpen: false,
    setIsAudioDialogOpen: (open) => set({ isAudioDialogOpen: open }),

    setSelectedSessionId: (id) => set({ selectedSessionId: id }),
    setSelectedDocId: (id) => set({ selectedDocId: id }),
    setIsSessionSelected: (selected) => set({ isSessionSelected: selected }),
    setSessions: (sessions) => set({ sessions }),

    clearSelectedSession: () => set({
        sessionData: null,
        isSessionSelected: false,
        selectedSessionId: null,
        selectedDocId: null,
    }),

    upsertSessionInList: (session) => {
        const tempStatusStore = getTempStatusStore();
        const resolvedSession = applyTempStatusResolution(session, tempStatusStore);
        localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusStore));
        set((state) => {
            const existingIndex = state.sessions.findIndex((s) => s.id === resolvedSession.id);
            const nextSessions = existingIndex >= 0
                ? state.sessions.map((s) => (s.id === resolvedSession.id ? resolvedSession : s))
                : [...state.sessions, resolvedSession];
            return { sessions: sortSessionsByDateDesc(nextSessions) };
        });
    },

    removeSessionFromList: (id) => {
        removeTempStatusMetadata(id);
        set((state) => ({
            sessions: state.sessions.filter((s) => s.id !== id),
        }));
    },

    fetchSessionListItem: async (id) => {
        try {
            const response = await axios.get(`/api/apps/fetch-session-list-item/${id}/`);
            if (response.status === 200) {
                const sessionItem = response.data as SessionListData;
                get().upsertSessionInList(sessionItem);
                return sessionItem;
            }
        } catch (error) {
            if (logLevel >= 1) console.error('❌ Fehler beim Laden des Session-Listeneintrags:', error);
        }
        return null;
    },


    fetchSessionDataById: async (id, docId) => {
        
        try {
            const currentState = get();
            const isSameSessionReload = currentState.selectedSessionId === id || currentState.sessionData?.id === id;
            const currentDocId = isSameSessionReload ? currentState.selectedDocId : null;
            const effectiveDocId = docId ?? currentDocId ?? null;
            let response;
            try {
                response = await axios.get(`/api/apps/fetch-session/${id}`, effectiveDocId ? { params: { doc_id: effectiveDocId } } : undefined);
            } catch (error) {
                if (!docId && effectiveDocId && axios.isAxiosError(error) && error.response?.status === 404) {
                    response = await axios.get(`/api/apps/fetch-session/${id}`);
                } else {
                    throw error;
                }
            }
            if (response.status === 200) {
                let sessionData = response.data;
                let selectedNewDoc = false;
                if (!docId && isSameSessionReload && currentDocId) {
                    const previousDocIds = new Set((currentState.sessionData?.documentation_list ?? []).map((doc) => doc.id));
                    const newDoc = sessionData?.documentation_list?.find((doc: DocumentationMeta) => !previousDocIds.has(doc.id));
                    if (newDoc) {
                        const newDocResponse = await axios.get(`/api/apps/fetch-session/${id}`, { params: { doc_id: newDoc.id } });
                        if (newDocResponse.status === 200) {
                            sessionData = newDocResponse.data;
                            selectedNewDoc = true;
                        }
                    }
                }
                const backendDocId = sessionData?.documentation?.id ?? null;
                const docStillExists = !!(
                    !docId &&
                    !selectedNewDoc &&
                    isSameSessionReload &&
                    currentDocId &&
                    sessionData?.documentation_list?.some((doc: DocumentationMeta) => doc.id === currentDocId)
                );
                const selectedDocId = docStillExists ? currentDocId : backendDocId;
                if (logLevel >= 3) console.log('✅ Sitzung geladen:', sessionData);
                set({ sessionData, isSessionSelected: true, selectedDocId }); // ✅ Sitzung als ausgewählt markieren
                return sessionData;
            }
        } catch (error) {
            if (logLevel >= 1) console.error('❌ Fehler beim Laden der Sitzung:', error);
        }
        return null;
    },

    fetchSessions: async () => {
        
        try {
            const response = await axios.get('/api/apps/fetch-sessions/');
            if (response.status === 200) {
                const data = sortSessionsByDateDesc(response.data);
                const tempStatusMap = getTempStatusStore();
                const enriched = data.map((s: SessionListData) => applyTempStatusResolution(s, tempStatusMap));
                localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusMap));
                set({ sessions: enriched });
                return enriched;
            }
        } catch (error) {
            if (logLevel >= 1) console.error("❌ Fehler beim Laden aller Sitzungen:", error);
        }
        return null;
    },

    updateSessionTempStatus: (id: number, status: string) => {
        const tempStatusStore = JSON.parse(localStorage.getItem('tempStatusMap') || '{}');
        tempStatusStore[id] = status;
        localStorage.setItem('tempStatusMap', JSON.stringify(tempStatusStore));
        const timestampKey = `tempStatusTime_${id}`;
        localStorage.setItem(timestampKey, Date.now().toString());
        const expirationKey = `tempStatusExpire_${id}`;
        const durationMinutes = expirationMinutes[status] || 30;
        const expirationTime = Date.now() + durationMinutes * 60 * 1000;
        localStorage.setItem(expirationKey, expirationTime.toString());

        set((state) => ({
            sessions: state.sessions.map((s) =>
                s.id === id ? { ...s, tempStatus: status } : s
            ),
        }));
    },

    clearTempStatus: (id: number) => {
        removeTempStatusMetadata(id);
    },

    reset: () => set(() => ({
        sessionData: null,
        sessions: [],
        isSessionSelected: false,
        selectedSessionId: null,
        selectedDocId: null,
        isAudioDialogOpen: false
    })),

    // ✅ Generische Update-Funktion für Sitzungen
    updateSession: async (id, updatedFields) => {
        
        try {
            const response = await axios.put('/api/apps/update-session/', { id, ...updatedFields });

            if (response.status === 200) {
                if (logLevel >= 3) console.log('✅ Sitzung aktualisiert:', updatedFields);
                set((state) => ({
                    sessionData: state.sessionData
                        ? { ...state.sessionData, ...updatedFields }
                        : null,
                }));
                return true;
            }
        } catch (error) {
            if (logLevel >= 1) console.error('❌ Fehler beim Aktualisieren der Sitzung:', error);
        }
        return false;
    }
}));


// Mapping-Objekte für Anzeigezwecke
export const labelMap: Record<string, string> = {
    // Labels für Doku Anzeige
    male: 'Männlich',
    female: 'Weiblich',
    client: 'Klient',
    patient: 'Patient',
    customer: 'Kunde',
    in_person: 'Persönlich',
    online: 'Online',
    clients_home: 'Hausbesuch',
    intake: 'Erstgespräch',
    progress_note: 'Verlaufsdokumentation',
    summary: 'Zusammenfassung',
    supervision: 'Supervision',
    individual: 'Einzel',
    couple: 'Paar',
    group: 'Gruppe',
    dictate: 'Diktat',
    recording: 'Live-Aufnahme',
    upload: 'Upload',
    discribtion: 'Beschreibung',

    // Labels für Statusanzeige
    transcribing: 'Audio wird transkribiert...',
    failed: 'Anfrage fehlgeschlagen',
    completed: 'Abgeschlossen',
    processing: 'Anfrage wird verarbeitet ...',
    waiting: 'Anfrage wird vorbereitet ...',
    unknown: 'Unbekannt',
    generating: 'Wird generiert...',
};
